import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import { MonetizationType, type ObjectType } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { LinearTitleOffer } from '@/interfaces/buybox';
import GridOffer from '@/features/buybox/components/grid/GridOffer.vue';
import { useMounted, useNow } from '@vueuse/core';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { useExperiment } from '@/libraries/experiment';
import { sortAndDedupeOfferEpisodes, useLinearBroadcastTime } from '@/components/experiments/LinearTVExp';


export default /*#__PURE__*/_defineComponent({
  __name: 'LinearOfferExp',
  props: {
    title: null,
    offer: null,
    linearOffers: null,
    objectType: null,
    additionalContexts: null,
    inline: { type: Boolean },
    isBannerBuyBox: { type: Boolean }
  },
  emits: ["clickOut"],
  setup(__props: any) {

const props = __props as {
	title: TitleDetail;
	offer: LinearTitleOffer;
	linearOffers: LinearTitleOffer[];
	objectType: ObjectType;
	additionalContexts?: SnowplowContext[];
	inline?: boolean;
	isBannerBuyBox?: boolean;
};





const isMounted = useMounted();
const { country, language } = storeToRefs(useLanguageStore());
const { broadcastTimeZone } = useLinearBroadcastTime();

// Live status
const now = useNow({ interval: 60000 });

const liveStatus = computed(() => {
	if (!isMounted.value) return false;

	const { availableFromTime, availableToTime } = props.offer;
	if (!availableFromTime || !availableToTime) return false;

	const from = new Date(availableFromTime).getTime();
	const to = new Date(availableToTime).getTime();
	if (Number.isNaN(from) || Number.isNaN(to)) return false;

	const nowMs = now.value.getTime();
	return from <= nowMs && nowMs < to;
});

const additionalEpisodes = computed(() => {
	return sortAndDedupeOfferEpisodes(props.offer.offerEpisodes ?? []).slice(1);
});

function getDate(dateTime: string | Date | null | undefined) {
	if (!dateTime) return '';
	const parsed = new Date(dateTime);
	if (isNaN(parsed.getTime())) return '';

	return new Intl.DateTimeFormat(`${language.value.split('-')[0]}-${country.value}`, {
		day: '2-digit',
		month: '2-digit',
		timeZone: broadcastTimeZone.value,
	}).format(parsed);
}

function getTime(dateTime: string | Date | null | undefined) {
	if (!dateTime) return '';
	const parsed = new Date(dateTime);
	if (isNaN(parsed.getTime())) return '';

	return new Intl.DateTimeFormat(`${language.value.split('-')[0]}-${country.value}`, {
		hour: '2-digit',
		minute: '2-digit',
		timeZone: broadcastTimeZone.value,
		timeZoneName: 'short',
		hourCycle: 'h23',
	}).format(parsed);
}

function showFreeRibbon(offer: LinearTitleOffer) {
	return (
		[MonetizationType.Fast, MonetizationType.LinearFree].includes(offer.monetizationType) &&
		offer.package.packageId === 2738
	);
}

const { trackEvent } = useExperiment('EXP_LINEAR_TV_2');

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

function onVisibilityChanged(isVisible: boolean) {
	if (isVisible) trackEvent({ action: 'impression' });
}

return { __sfc: true,props, isMounted, country, language, broadcastTimeZone, now, liveStatus, additionalEpisodes, getDate, getTime, showFreeRibbon, trackEvent, observeVisibilityOptions, onVisibilityChanged, GridOffer }
}

})